import React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
// components
import Tooltip from '@mui/material/Tooltip'
import { CloseIcon } from 'components/Modals/GroupSecurityModal/Form/BusinessRule/views'
import { ReduxSelect } from 'components/FormElements/ReduxForm'
import BooleanMap from './BooleanMap'
import FixedValue from './FixedValue'
import RecordMap from './RecordMap'
import ValueMap from './ValueMap'
// constants
import { Forms } from 'constants/forms'
// types
import { IOption } from 'components/FormElements/Select'
import {
  IField,
  FieldType,
  IRiskTemplate,
  InjuryMasterPropertyKind,
  IInjuryMasterIncidentProperty
} from 'types/graphql'
import { describeCompatibleFields, FieldOption, isFieldCompatible, toFieldOption, toPropertyOption } from './utils'
// utils
import { FIXED_VALUE_SOURCE } from 'utils/systemProcess/injuryMaster'
import { required } from 'utils/validators'
// views
import { Hint, InfoIcon, PropertySelect, Row, SourceFieldSelect } from './views'

interface Props {
  // e.g. transactions[0].propertyMappings[1]
  name: string
  // e.g. transactions[0].propertyMappings
  arrayName: string
  properties: IInjuryMasterIncidentProperty[]
  templateFields: IField[]
  // Only for a parent case transaction, whose mappings may also read the parent form's fields
  parentTemplates: IRiskTemplate[]
  removeRow: () => void
}

interface IMappingValue {
  property?: IOption
  source?: IOption
  sourceField?: FieldOption
  fixedValue?: IOption | string
}

const SOURCE_OPTIONS: IOption[] = [
  { value: 'FIELD', label: 'A Skefto field' },
  { value: FIXED_VALUE_SOURCE, label: 'A fixed value' }
]

const selector = formValueSelector(Forms.SYSTEM_PROCESS_CREATE_FORM)

const MappedProperty: React.FC<Props> = ({
  name,
  removeRow,
  arrayName,
  properties,
  templateFields,
  parentTemplates
}) => {
  const dispatch = useDispatch()

  const mapping: IMappingValue | undefined = useSelector((state) => selector(state, name))
  const allMappings: IMappingValue[] = useSelector((state) => selector(state, arrayName)) ?? []

  const property = properties.find((item) => item.property === mapping?.property?.value)

  const propertyOptions: IOption[] = React.useMemo(() => {
    const used = allMappings.map((item) => item?.property?.value)

    return properties
      .filter((item) => item.property === property?.property || !used.includes(item.property))
      .map(toPropertyOption)
  }, [allMappings, properties, property])

  const fieldOptions: FieldOption[] = React.useMemo(
    () =>
      templateFields
        .filter((field) => isFieldCompatible(property, field))
        .map((field) => toFieldOption(templateFields, field, parentTemplates)),
    [property, templateFields, parentTemplates]
  )

  // Saved mappings only hold the property key — show its name once the catalog has loaded.
  React.useEffect(() => {
    if (property && mapping?.property?.label === mapping?.property?.value) {
      dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${name}.property`, toPropertyOption(property)))
    }
  }, [dispatch, mapping, name, property])

  // A property that can take a fixed value shows the choice; reading a field is the default.
  React.useEffect(() => {
    if (property?.supportsFixedValue && !mapping?.source) {
      dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${name}.source`, SOURCE_OPTIONS[0]))
    }
  }, [dispatch, mapping, name, property])

  const clear = (...keys: string[]) =>
    keys.forEach((key) => dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${name}.${key}`, undefined)))

  // A field, fixed value or value map chosen for another property doesn't carry over.
  const onPropertyChange = (option: IOption) => {
    const next = properties.find((item) => item.property === option?.value)
    const current = mapping?.sourceField?.field

    if (current && !isFieldCompatible(next, current)) {
      clear('sourceField')
    }

    clear('valueMap', 'recordMap', 'fixedValue')

    if (!next?.supportsFixedValue) {
      clear('source')
    }
  }

  const onSourceKindChange = () => clear('sourceField', 'valueMap', 'recordMap', 'fixedValue')

  const onSourceFieldChange = () => clear('valueMap', 'recordMap')

  // Required properties are added by MappedProperties and stay put — they can't be swapped or removed.
  const isRequired = !!property?.requiredOnCreate

  const isFixed = !!property?.supportsFixedValue && mapping?.source?.value === FIXED_VALUE_SOURCE
  const sourceField = isFixed ? undefined : mapping?.sourceField?.field

  // Kept to one line under the select — the compatible field types are in the property's info tooltip.
  const noFieldMessage = parentTemplates.length
    ? 'No compatible field in this form or its parent'
    : 'No compatible field in this form'
  const noFieldError = property && !fieldOptions.length ? noFieldMessage : undefined

  // With nothing to pick, the field's own error replaces `required` — ReduxSelect shows the
  // validation error over errorText once touched, so a plain "Required" would hide the reason.
  // Memoized: a new validate function on every render makes redux-form re-register the field.
  const validateSourceField = React.useMemo(() => (noFieldError ? () => noFieldError : required), [noFieldError])

  return (
    <div data-testid='injury-master-mapped-property'>
      <Row>
        <PropertySelect>
          <ReduxSelect
            validate={required}
            menuPortalTarget={null}
            label='Injury Master property'
            name={`${name}.property`}
            options={propertyOptions}
            isDisabled={isRequired}
            onSelectChange={onPropertyChange}
          />
          {property && (
            <Tooltip
              title={
                <>
                  {property.hint}
                  <div>
                    Fills from: {describeCompatibleFields(property)}
                    {property.requiredOnCreate ? ' · Required to create the case' : ''}
                  </div>
                </>
              }>
              <InfoIcon data-testid='injury-master-mapped-property-info' />
            </Tooltip>
          )}
        </PropertySelect>
        {property?.supportsFixedValue && (
          <ReduxSelect
            menuPortalTarget={null}
            label='Value comes from'
            name={`${name}.source`}
            options={SOURCE_OPTIONS}
            onSelectChange={onSourceKindChange}
          />
        )}
        {isFixed && property ? (
          // Keyed by property: switching to a different property must remount, not reuse, this —
          // otherwise a RecordPicker inside it keeps its previous property's loaded options and its
          // "already fetched" flag, and shows the wrong list until manually refreshed.
          <FixedValue key={property.property} name={name} property={property} value={mapping?.fixedValue} />
        ) : (
          <SourceFieldSelect>
            <ReduxSelect
              validate={validateSourceField}
              menuPortalTarget={null}
              label='Skefto field'
              name={`${name}.sourceField`}
              options={fieldOptions}
              isDisabled={!property}
              onSelectChange={onSourceFieldChange}
              errorText={noFieldError}
            />
          </SourceFieldSelect>
        )}
        <CloseIcon
          data-testid='injury-master-mapped-property-remove'
          onClick={removeRow}
          // Hidden rather than dropped, so the row's selects stay aligned with the removable rows.
          style={isRequired ? { visibility: 'hidden' } : undefined}
        />
      </Row>
      {property?.kind === InjuryMasterPropertyKind.Choice && sourceField?.fieldType === FieldType.PickList && (
        <ValueMap name={name} property={property} sourceField={sourceField} />
      )}
      {property?.kind === InjuryMasterPropertyKind.Boolean && sourceField?.fieldType === FieldType.PickList && (
        <BooleanMap name={name} property={property} sourceField={sourceField} />
      )}
      {property?.kind === InjuryMasterPropertyKind.Choice &&
        sourceField &&
        sourceField.fieldType !== FieldType.PickList && (
          <Hint>The value is matched to an Injury Master option by name.</Hint>
        )}
      {property?.kind === InjuryMasterPropertyKind.Record && sourceField?.fieldType === FieldType.PickList && (
        // Keyed by property + source field, for the same reason as FixedValue above — a new pairing
        // must start with a clean set of RecordPickers, not the previous pairing's loaded state.
        <RecordMap
          key={`${property.property}:${sourceField.id}`}
          name={name}
          property={property}
          sourceField={sourceField}
        />
      )}
    </div>
  )
}

export default MappedProperty
